/* eslint-disable @typescript-eslint/no-use-before-define */

import React from 'react';

import {
  takeLatest,
  take,
  call,
  put,
  select,
  all,
  delay,
  fork,
  cancel,
} from 'redux-saga/effects';
import { push } from 'connected-react-router';

// Store
import store, { persistor } from 'store';

// Services
import { myFirebase } from 'services/firebase';
import {
  webUser,
  appUser,
  appUserDocument,
  appUserComment,
  appUserSettings,
} from 'services/apiDigitalTicket/users';

// Components
import { ModalBulkErrors } from 'components/ModalBulkErrors';

// Actions
import * as SnackbarsActions from 'store/ducks/snackbars/actions';
import * as DialogsActions from 'store/ducks/dialogs/actions';
import * as OrdersActions from 'store/ducks/orders/actions';

// Helpers
import { setToken, setId, getId, setClaims } from 'helpers/auth';
import { isObject } from 'helpers/isObject';

import { redirectToModule } from 'helpers/redirectToModule';

// Constants
import { errorMessagersList } from 'helpers/errorMessagers';
import PATH from 'constants/basePath';
import ENVS from 'constants/enviroments';
import CONST_USER_PROFILE from 'constants/userProfile';
import {
  getAuth,
  signInWithEmailAndPassword,
  getIdToken,
  getIdTokenResult,
  verifyPasswordResetCode,
  sendPasswordResetEmail,
  confirmPasswordReset,
  TotpMultiFactorGenerator,
  multiFactor,
  getMultiFactorResolver,
} from 'firebase/auth';

// Types
import { UsersTypes, AppUser, UserDocumentsInterface } from './types';

// Components
import * as UsersActions from './actions';

let refreshTask: any;

function* authLogin({
  payload,
}: ReturnType<typeof UsersActions.loginRequest>): any {
  try {
    yield put(UsersActions.logoutRequest(null));
    yield put(DialogsActions.setOpenLoading());

    const auth = getAuth(myFirebase);
    let response: any;

    try {
      response = yield call(() =>
        signInWithEmailAndPassword(auth, payload.email, payload.password),
      );

      // If we get here, it means no MFA is required
      const token = yield call(() => getIdToken(response.user, true));
      const tokenResult = yield call(() => getIdTokenResult(response.user));
      const userId = response.user.uid;

      setId(userId);
      setToken(token);
      setClaims(tokenResult.claims);

      if (refreshTask) {
        yield cancel(refreshTask);
      }

      refreshTask = yield fork(refreshTokenSaga);

      yield put(
        UsersActions.loginSuccess({
          ...response.user,
          mfaEnabled: false,
        }),
      );

      if (tokenResult.claims.types) {
        yield put(DialogsActions.setClose());
        const userTypes = tokenResult.claims.types;
        if (userTypes.length > 1) {
          yield put(push('/selecione-um-modulo'));
        } else {
          redirectToModule(userTypes[0].type, userTypes[0].CompanyID);
        }
      }
    } catch (error) {
      if (error.code === 'auth/multi-factor-auth-required') {
        // Handle MFA
        const resolver = getMultiFactorResolver(auth, error);
        const totpHint = resolver.hints.find(
          (hint: any) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
        );

        if (totpHint) {
          yield put(UsersActions.setMFAResolver(resolver));
          yield put(DialogsActions.setClose());
        }
      } else {
        // Handle actual login errors
        yield put(DialogsActions.setClose());
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'error',
            message: 'Email ou senha incorretos',
          }),
        );
        return;
      }
    }
  } catch (error) {
    console.error('Login error:', error);
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Erro no Login',
      }),
    );
  }
}

function* enrollMFA({
  payload,
}: ReturnType<typeof UsersActions.enrollMFARequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading());

    const auth = getAuth(myFirebase);
    const user = auth.currentUser;

    if (!user) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Usuário não encontrado',
        }),
      );

      throw new Error('Usuário não encontrado');
    }

    const multiFactorUser = multiFactor(user);
    const session = yield call(() => multiFactorUser.getSession());

    // Generate TOTP secret
    const totpGeneratorResponse = yield call(() =>
      TotpMultiFactorGenerator.generateSecret(session),
    );

    // Generate QR code URL using otpauth URI format
    const qrCodeUrl = `otpauth://totp/${encodeURIComponent(
      user?.email ? user?.email : '',
    )}?secret=${totpGeneratorResponse.secretKey}&issuer=${encodeURIComponent(
      'BDOS',
    )}`;

    const totpSecret = {
      qrCodeUrl,
      secretKey: totpGeneratorResponse.secretKey,
      // Store the entire TOTP response, we'll need it for verification
      totpGeneratorResponse,
    };

    yield put(UsersActions.enrollMFASuccess(totpSecret));
    yield put(DialogsActions.setClose());
  } catch (error) {
    console.error('MFA enrollment error:', error);
    yield put(DialogsActions.setClose());
    yield put(UsersActions.enrollMFAFailure(error.message));
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Erro ao registrar MFA',
      }),
    );
  }
}

// Complete MFA enrollment with verification code
function* verifyAndEnrollMFA({
  payload,
}: ReturnType<typeof UsersActions.verifyMFAEnrollRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading());

    const auth = getAuth(myFirebase);
    const user = auth.currentUser;
    const totpSecret = yield select((state) => state.users.totpSecret);

    if (!user || !totpSecret || !totpSecret.totpGeneratorResponse) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Erro ao validar MFA. Tente novamente.',
        }),
      );

      throw new Error('Missing user, TOTP secret, or TOTP response');
    }

    const multiFactorUser = multiFactor(user);

    const enrollmentResponse = yield call(async () => {
      // Create the enrollment credential using the TOTP info and verification code
      const cred = TotpMultiFactorGenerator.assertionForEnrollment(
        totpSecret.totpGeneratorResponse, // Pass the entire TOTP response
        payload.code,
      );

      // Enroll the factor with the credential
      return multiFactorUser.enroll(cred, 'BDOS');
    });

    yield put(
      UsersActions.verifyMFAEnrollSuccess({
        user: {
          ...user,
          mfaEnabled: true,
        },
        isEnrolled: !!enrollmentResponse,
      }),
    );

    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'success',
        message: 'MFA habilitado com sucesso',
      }),
    );
  } catch (error) {
    console.error('MFA verification error:', error);
    yield put(DialogsActions.setClose());
    yield put(UsersActions.verifyMFAEnrollFailure(error.message));
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Erro ao validar o código MFA. Tente novamente.',
      }),
    );
  }
}

function* verifyMFASignIn({
  payload,
}: ReturnType<typeof UsersActions.verifyMFARequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading());

    const resolver = yield select((state) => state.users.mfaResolver);

    if (!resolver) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Resolver MFA não encontrado',
        }),
      );

      throw new Error('MFA resolver not found');
    }

    const totpHint = resolver.hints.find(
      (hint: any) => hint.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

    if (!totpHint) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Fator TOTP não encontrado',
        }),
      );

      throw new Error('TOTP factor not found');
    }

    // Create TOTP assertion for sign-in
    const assertion = yield call(() =>
      TotpMultiFactorGenerator.assertionForSignIn(totpHint.uid, payload.code),
    );

    // Resolve the sign-in
    const multiFactorAssertion = yield call(() =>
      resolver.resolveSignIn(assertion),
    );

    const { user } = multiFactorAssertion;

    // Get token and claims - same as authLogin
    const token = yield call(() => getIdToken(user, true));
    const tokenResult = yield call(() => getIdTokenResult(user));
    const userId = user.uid;

    // Set tokens and claims - same as authLogin
    setId(userId);
    setToken(token);
    setClaims(tokenResult.claims);

    if (refreshTask) {
      yield cancel(refreshTask);
    }

    refreshTask = yield fork(refreshTokenSaga);

    // Set login success
    yield put(
      UsersActions.loginSuccess({
        ...user,
        mfaEnabled: true,
      }),
    );

    // Handle redirection
    if (tokenResult.claims.types) {
      yield put(DialogsActions.setClose());
      const userTypes = tokenResult.claims.types;
      if (userTypes.length > 1) {
        yield call(
          setTimeout,
          () => {
            put(push('/selecione-um-modulo'));
          },
          1500,
        );
      } else {
        yield call(
          setTimeout,
          () => {
            redirectToModule(userTypes[0].type, userTypes[0].CompanyID);
          },
          1500,
        );
      }
    }

    // Clear MFA state
    yield put(UsersActions.verifyMFASuccess());
  } catch (error) {
    console.error('MFA sign-in verification error:', error);
    yield put(DialogsActions.setClose());
    yield put(UsersActions.verifyMFAFailure(error.message));
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Erro ao validar o código MFA. Tente novamente.',
      }),
    );
  }
}

function* unenrollMFA({
  payload,
}: ReturnType<typeof UsersActions.unenrollMFARequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading());

    const auth = getAuth(myFirebase);
    const user = auth.currentUser;

    if (!user) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Usuário não encontrado',
        }),
      );

      throw new Error('User must be logged in to disable MFA');
    }

    const multiFactorUser = multiFactor(user);
    const { enrolledFactors } = multiFactorUser;

    if (enrolledFactors.length === 0) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Nenhum fator MFA encontrado',
        }),
      );

      throw new Error('No MFA factors are currently enrolled');
    }

    // Find the TOTP factor to unenroll
    const totpFactor = enrolledFactors.find(
      (factor: any) => factor.factorId === TotpMultiFactorGenerator.FACTOR_ID,
    );

    if (!totpFactor) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message: 'Nenhum fator TOTP encontrado',
        }),
      );

      throw new Error('No TOTP factor found');
    }

    // Unenroll the TOTP factor
    const response = yield call(() => multiFactorUser.unenroll(totpFactor));

    // Get the updated resolver state after unenrollment
    const resolver = response?.resolver || null;

    yield put(
      UsersActions.unenrollMFASuccess({
        user: {
          ...user,
          mfaEnabled: false,
        },
        resolver,
      }),
    );

    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'success',
        message: 'MFA desativado com sucesso',
      }),
    );
  } catch (error) {
    console.error('MFA unenrollment error:', error);
    yield put(DialogsActions.setClose());
    yield put(UsersActions.unenrollMFAFailure(error.message));
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Erro ao desativar o MFA',
      }),
    );
  }
}

function* logout({
  payload,
}: ReturnType<typeof UsersActions.logoutRequest>): any {
  try {
    // Clear all redux-persist
    persistor.flush();

    if (refreshTask) {
      yield cancel(refreshTask);
    }
    yield put(
      UsersActions.logoutSuccess({
        mfaEnabled: false,
      }),
    );
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - logout - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message: 'Ops... não conseguimos fazer seu logout',
      }),
    );
    yield put(UsersActions.logoutFailure());
  }
}

function* resetPasswordVerify({
  payload,
}: ReturnType<typeof UsersActions.resetPasswordRequest>): any {
  try {
    const auth = getAuth(myFirebase); // Use the modular auth method
    yield put(DialogsActions.setOpenLoading());

    const response = yield call(() =>
      verifyPasswordResetCode(auth, payload.actionCode),
    );

    if (response) {
      yield put(
        UsersActions.verifyPasswordResetCodeSuccess({
          email: response,
        }),
      );
    } else {
      console.error('users - resetPasswordVerify - not found');
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'error',
          message:
            'Não conseguimos verificar o código de alteração de senha. Contate o suporte do sistema.',
        }),
      );
      yield put(UsersActions.verifyPasswordResetCodeFailure());
      // Empty continueUrl would reload this same page in an endless loop
      yield delay(5000);
      window.location.href = payload.continueUrl || PATH.LOGIN;
    }

    yield put(DialogsActions.setClose());
  } catch (error) {
    const firebaseCode: string = error && error.code;
    const isInvalidActionCode =
      firebaseCode === 'auth/invalid-action-code' ||
      firebaseCode === 'auth/expired-action-code';
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = isInvalidActionCode
      ? 'Este link de redefinição de senha é inválido ou já expirou. Solicite um novo link em "Esqueci minha senha".'
      : errorMessagersList(internalCode);

    console.error('users - resetPasswordVerify - error', error);
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.verifyPasswordResetCodeFailure());
    // Empty continueUrl would reload this same page in an endless loop
    yield delay(5000);
    window.location.href = payload.continueUrl || PATH.LOGIN;
  }
}

function* resetPassword({
  payload,
}: ReturnType<typeof UsersActions.resetPasswordRequest>): any {
  try {
    const auth = getAuth(myFirebase); // Use the modular auth method
    yield put(DialogsActions.setOpenLoading());

    // Use the modular confirmPasswordReset method
    yield call(() =>
      confirmPasswordReset(auth, payload.actionCode, payload.password),
    );

    yield put(UsersActions.resetPasswordSuccess());
    yield put(DialogsActions.setClose());
    // Keep the success screen visible before leaving; empty continueUrl
    // would reload this page with an already-consumed oobCode (loop)
    yield delay(3000);
    window.location.href = payload.continueUrl || PATH.LOGIN;
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - resetPassword - error', error);
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.resetPasswordFailure());
  }
}

function* forgotPassword({
  payload,
}: ReturnType<typeof UsersActions.forgotPasswordRequest>): any {
  try {
    const auth = getAuth(myFirebase); // Use the modular auth method
    yield put(DialogsActions.setOpenLoading());

    // Use the modular sendPasswordResetEmail function
    yield call(() =>
      sendPasswordResetEmail(auth, payload.email, {
        url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
      }),
    );

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'info',
        message: payload.internal
          ? 'Foi enviado para o e-mail um link para alterar a senha'
          : 'Foi enviado para você um e-mail com link para alterar sua senha',
      }),
    );

    yield put(UsersActions.forgotPasswordSuccess());
    yield put(DialogsActions.setClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - forgotPassword - error', error);
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.forgotPasswordFailure());
  }
}

function* createPassword({
  payload,
}: ReturnType<typeof UsersActions.createPasswordRequest>): any {
  try {
    const auth = getAuth(myFirebase); // Use the modular auth method
    yield put(DialogsActions.setOpenLoading());

    // Use the modular sendPasswordResetEmail function
    yield call(() =>
      sendPasswordResetEmail(auth, payload.email, {
        url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
      }),
    );

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'info',
        message: 'Foi enviado para o e-mail um link para definir a senha',
      }),
    );

    yield put(UsersActions.createPasswordSuccess());
    yield put(DialogsActions.setClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - createPassword - error', error);
    yield put(DialogsActions.setClose());
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.createPasswordFailure());
  }
}

// Web Users
function* listWebUsers({
  payload,
}: ReturnType<typeof UsersActions.listWebUsersRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando usuários' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(webUser.list, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.listWebUsersSuccess({
          webUsersList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      if (!filtering) yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - listWebUsers - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.listWebUsersFailure());
    yield put(DialogsActions.setClose());
  }
}

function* appUserCheckFacialList({
  payload,
}: ReturnType<typeof UsersActions.appUserCheckFacialListRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(appUser.appUserCheckFacial, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.appUserCheckFacialListSuccess({
          appUserCheckFacialList: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - appUserCheckFacialList - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.appUserCheckFacialListFailure());
    yield put(DialogsActions.setClose());
  }
}

function* listFromModulesWebUsers({
  payload,
}: ReturnType<typeof UsersActions.listFromModulesWebUsersRequest>): any {
  try {
    const { body, filtering } = payload;
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando usuários' }),
      );
    }
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(webUser.listFromModules, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.listFromModulesWebUsersSuccess({
          webUsersListFromModules: response.data,
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - listWebUsers - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.listFromModulesWebUsersFailure());
    yield put(DialogsActions.setClose());
  }
}

function* filterWebUsers({
  payload,
  meta,
}: ReturnType<typeof UsersActions.filterWebUsersRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(webUser.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.filterWebUsersSuccess({
          webUsersList: response.data,
          totalCount: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - filterWebUsers - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    meta.promise.reject(error);
    yield put(UsersActions.filterWebUsersFailure());
  }
}

function* filterAppUsers({
  payload,
  meta,
}: ReturnType<typeof UsersActions.filterAppUsersRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(appUser.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    // eslint-disable-next-line no-underscore-dangle
    const includes = payload?._includes || '';
    const hasUserProfile = includes.toLowerCase().includes('userprofile');

    if (response.status === 200) {
      if (hasUserProfile) {
        yield put(
          UsersActions.filterAppUsersSuccess({
            appUsersList: (isObject(response.data)
              ? [response.data]
              : response.data
            ).map((user: AppUser) => ({
              ...user,
              profile: Array.isArray(user.UserProfiles)
                ? user.UserProfiles.map(({ Profile }) => Profile?.name).join(
                  ', ',
                )
                : '',
            })),
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      } else {
        yield put(
          UsersActions.filterAppUsersSuccess({
            appUsersList: (isObject(response.data)
              ? [response.data]
              : response.data
            ).map((user: AppUser) => ({
              ...user,
              profile: Array.isArray(user.Profiles)
                ? user.Profiles.map(({ name }) => name).join(', ')
                : '',
            })),
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      }
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - filterAppUsers - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    meta.promise.reject(error);
    yield put(UsersActions.filterAppUsersFailure());
  }
}

function* createWebUser({
  payload,
}: ReturnType<typeof UsersActions.createWebUserRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando usuário(s)' }),
    );

    const response = yield call(webUser.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      const auth = getAuth(myFirebase);

      yield all(
        [...response.data.success].map((user: any) =>
          call(() =>
            sendPasswordResetEmail(auth, user.email, {
              url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
            }),
          ),
        ),
      );

      yield put(DialogsActions.setClose());

      if (response.data.error.length > 0) {
        const ModalErrors = React.createElement(ModalBulkErrors, {
          success: response.data.success.length,
          errors: response.data.error.map((element: any) => {
            const message = errorMessagersList(element.error);
            return {
              primaryText: element.name,
              secondaryText: message,
            };
          }),
        });

        yield put(
          DialogsActions.setOpen({
            open: true,
            content: ModalErrors,
            onClose: () => {
              store.dispatch(
                UsersActions.listWebUsersRequest({
                  body: { _includes: 'operators' },
                }),
              );
            },
          }),
        );
      } else {
        yield put(
          SnackbarsActions.setOpen({
            open: true,
            type: 'success',
            message: 'Usuário(s) cadastrado(s) com sucesso',
          }),
        );
        yield put(
          UsersActions.listWebUsersRequest({
            body: {
              _includes: 'operators',
            },
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - createWebUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.createWebUserFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getWebUser({
  payload,
}: ReturnType<typeof UsersActions.getWebUserRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));

    const response = yield call(webUser.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.getWebUserSuccess({
          webUserData: response.data,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - getWebUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.getWebUserFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateWebUser({
  payload,
}: ReturnType<typeof UsersActions.listWebUsersRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    yield put(DialogsActions.setOpenLoading({ title: 'Editando usuário' }));

    const response = yield call(webUser.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.updateWebUserSuccess({
          webUserData: Object.assign(response.data, { isUpdate: true }),
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Supervisor alterado com sucesso',
        }),
      );
      if (!payload.filtering) {
        yield put(
          UsersActions.listWebUsersRequest({
            body: {
              _includes: 'operators',
            },
          }),
        );
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateWebUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.updateWebUserFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateWebUserEmail({
  payload,
}: ReturnType<typeof UsersActions.updateWebUserEmailRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    yield put(DialogsActions.setOpenLoading({ title: 'Alterando e-mail' }));

    const response = yield call(webUser.updateEmail, {
      data: {
        transportOperatorId: transportOperator.id,
        userId: payload.userId,
        email: payload.email,
      },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.updateWebUserEmailSuccess({
          webUserData: response.data,
        }),
      );

      // O token atual segue válido, mas a sessão só mostra o novo e-mail
      // após novo login
      const isLoggedUser = getId() === payload.userId;
      const loggedUserWarning =
        'E-mail alterado com sucesso. Saia e entre novamente com o novo e-mail';

      if (payload.webUserUpdate) {
        // Segue com os demais dados (nome, foto) pelo fluxo atual do webUserUpdate
        yield put(UsersActions.updateWebUserRequest(payload.webUserUpdate));
        const result = yield take([
          UsersTypes.UPDATE_WEB_USER_SUCCESS,
          UsersTypes.UPDATE_WEB_USER_FAILURE,
        ]);

        if (result.type === UsersTypes.UPDATE_WEB_USER_FAILURE) {
          // O e-mail já foi trocado: garante que a lista reflita o novo e-mail
          yield put(
            UsersActions.listWebUsersRequest({
              body: {
                _includes: 'operators',
              },
            }),
          );
        } else if (isLoggedUser) {
          yield put(
            SnackbarsActions.setOpen({
              open: true,
              type: 'warning',
              message: loggedUserWarning,
            }),
          );
        }
        return;
      }

      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: isLoggedUser ? 'warning' : 'success',
          message: isLoggedUser
            ? loggedUserWarning
            : 'E-mail alterado com sucesso',
        }),
      );
      yield put(
        UsersActions.listWebUsersRequest({
          body: {
            _includes: 'operators',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateWebUserEmail - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.updateWebUserEmailFailure());
    yield put(DialogsActions.setClose());
  }
}

function* delWebUser({
  payload,
}: ReturnType<typeof UsersActions.deleteWebUserRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo usuário' }));
    const response = yield call(webUser.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Usuário excluído com sucesso',
        }),
      );
      yield put(
        UsersActions.listWebUsersRequest({
          body: {
            _includes: 'operators',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('WebUser - delWebUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.listWebUsersFailure());
    yield put(DialogsActions.setClose());
  }
}

// App Users
function* listAppUsers({
  payload,
}: ReturnType<typeof UsersActions.listAppUsersRequest>): any {
  try {
    const { body, filtering, isCsvFull, userProfile } = payload;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando passageiros' }),
      );
    }
    const response = yield call(appUser.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      if (isObject(response.data)) {
        yield put(
          UsersActions.listAppUsersSuccess({
            appUsersList: response.data,
            appUsersListFull: response.data,
            isCsvFull,
            totalCount: parseInt(response.headers.totalcount, 10),
          }),
        );
      }

      if (Array.isArray(response.data)) {
        const [DOCUMENT_CPF] = CONST_USER_PROFILE.RESOURCES_TYPE;
        const findDocument = (documents: UserDocumentsInterface[]): string => {
          let document = '';
          if (Array.isArray(documents)) {
            const foundCPF = documents.find(
              (doc) => doc.type === DOCUMENT_CPF.TYPE,
            );

            if (foundCPF) document = foundCPF.document;
            else {
              document =
                documents.find((doc) => doc.type !== DOCUMENT_CPF.TYPE)
                  ?.document || 'Sem documento';
            }
          }
          return document;
        };

        if (userProfile) {
          yield put(
            UsersActions.listAppUsersSuccess({
              appUsersList: response.data.map((user: AppUser) => ({
                ...user,
                profile: Array.isArray(user.UserProfiles)
                  ? user.UserProfiles.map(({ Profile }) => Profile?.name).join(
                    ', ',
                  )
                  : '',
                document: findDocument(user?.UserDocuments),
              })),
              appUsersListFull: response.data.map((user: AppUser) => ({
                ...user,
                profile: Array.isArray(user.UserProfiles)
                  ? user.UserProfiles.map(({ Profile }) => Profile?.name).join(
                    ', ',
                  )
                  : '',
                document: findDocument(user?.UserDocuments),
              })),
              isCsvFull,
              totalCount: parseInt(response.headers.totalcount, 10),
            }),
          );
        } else {
          yield put(
            UsersActions.listAppUsersSuccess({
              appUsersList: response.data.map((user: AppUser) => ({
                ...user,
                profile: Array.isArray(user.Profiles)
                  ? user.Profiles.map(({ name }) => name).join(', ')
                  : '',
                document: findDocument(user?.UserDocuments),
              })),
              appUsersListFull: response.data.map((user: AppUser) => ({
                ...user,
                profile: Array.isArray(user.Profiles)
                  ? user.Profiles.map(({ name }) => name).join(', ')
                  : '',
                document: findDocument(user?.UserDocuments),
              })),
              isCsvFull,
              totalCount: parseInt(response.headers.totalcount, 10),
            }),
          );
        }
      }

      if (!filtering) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - listAppUsers - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.listAppUsersFailure());

    if (!payload.filtering) {
      yield put(DialogsActions.setClose());
    }
  }
}

// App Users Merge
function* listAppUsersMerge({
  payload,
}: ReturnType<typeof UsersActions.listAppUsersMergeRequest>): any {
  try {
    const { body, filtering } = payload;
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    if (!filtering) {
      yield put(
        DialogsActions.setOpenLoading({ title: 'Carregando passageiros' }),
      );
    }
    const response = yield call(appUser.get, {
      params: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.listAppUsersMergeSuccess({
          appUsersMergeList: response.data,
          totalCountMerge: parseInt(response.headers.totalcount, 10),
        }),
      );

      if (!filtering) {
        yield put(DialogsActions.setClose());
      }
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - listAppUsersMerge - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.listAppUsersMergeFailure());

    if (!payload.filtering) {
      yield put(DialogsActions.setClose());
    }
  }
}

function* filterAppUsersMerge({
  payload,
  meta,
}: ReturnType<typeof UsersActions.filterAppUsersMergeRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(appUser.list, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.filterAppUsersMergeSuccess({
          appUsersMergeList: (isObject(response.data)
            ? [response.data]
            : response.data
          ).map((user: AppUser) => ({
            ...user,
            profile: Array.isArray(user.Profiles)
              ? user.Profiles.map(({ name }) => name).join(', ')
              : '',
          })),
          totalCountMerge: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response.data);
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - filterAppUsersMerge - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    meta.promise.reject(error);
    yield put(UsersActions.filterAppUsersMergeFailure());
  }
}

function* getAppUserMerge({
  payload,
}: ReturnType<typeof UsersActions.getAppUserMergeRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const response = yield call(appUser.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.getAppUserMergeSuccess({
          appUserMergeData: response.data,
        }),
      );
    }

    yield put(DialogsActions.setClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - getAppUserMerge - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.getAppUserMergeFailure());
    yield put(DialogsActions.setClose());
  }
}

function* appUserMerge({
  payload,
  meta,
}: ReturnType<typeof UsersActions.appUserMergeRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    yield put(
      DialogsActions.setOpenLoading({ title: 'Carregando passageiros' }),
    );

    const response = yield call(appUser.merge, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.appUserMergeSuccess({
          appUsersMergeList: response.data,
          totalCountMerge: parseInt(response.headers.totalcount, 10),
        }),
      );
      meta.promise.resolve(response);
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - appUserMerge - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );

    yield put(UsersActions.setAppUserMergeError(true));

    yield put(UsersActions.appUserMergeFailure());

    yield put(DialogsActions.setClose());
  }
}

function* getAppUser({
  payload,
}: ReturnType<typeof UsersActions.getAppUserRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUser.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.getAppUserSuccess({
          appUserData: response.data,
        }),
      );
    }

    yield put(DialogsActions.setClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - getAppUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.getAppUserFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getAppUserFromDocument({
  payload,
  meta,
}: ReturnType<typeof UsersActions.getAppUserFromDocumentRequest>): any {
  // Attention! This sagas should not have any feedback (Loading or Snackbar)
  try {
    const response = yield call(appUser.get, { params: payload });

    if (response.status === 200) {
      yield put(
        UsersActions.getAppUserFromDocumentSuccess({
          appUserData: response.data,
        }),
      );
      meta.promise.resolve(response);
    } else {
      yield put(UsersActions.getAppUserFromDocumentFailure());
    }
  } catch (error) {
    console.error('users - getAppUserFromDocument - error', error);
    yield put(UsersActions.getAppUserFromDocumentFailure());
  }
}

function* getAppUserInConpanyByDocument({
  payload,
}: ReturnType<typeof UsersActions.getAppUserInCompanyByDocumentRequest>): any {
  // Attention! This sagas should not have any feedback (Loading or Snackbar)
  try {
    const response = yield call(appUser.getByDocument, { params: payload });

    if (response.status === 200) {
      yield put(
        UsersActions.getAppUserInCompanyByDocumentSuccess({
          appUserInCompanyData: response.data,
        }),
      );
    } else {
      yield put(UsersActions.getAppUserInCompanyByDocumentFailure());
    }
  } catch (error) {
    console.error('users - getAppUserInCompanyByDocument - error', error);
    yield put(UsersActions.getAppUserInCompanyByDocumentFailure());
  }
}

function* createAppUser({
  payload,
}: ReturnType<typeof UsersActions.createAppUserRequest>): any {
  try {
    const { noRedirect, body } = payload;

    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando passageiro' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUser.create, {
      data: { transportOperatorId: transportOperator.id, ...body },
    });

    if (response.status === 200) {
      if (body.email) {
        const auth = getAuth(myFirebase);

        yield call(() =>
          sendPasswordResetEmail(auth, response.data.email, {
            url: ENVS.PASSWORD_REDIRECT || 'https://bilhetagemdigital.com.br',
          }),
        );
      }

      yield put(
        UsersActions.createAppUserSuccess({
          appUserData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: `Passageiro cadastrado com sucesso. ${body.email ? 'E-mail de definição de senha enviado.' : ''
            }`,
        }),
      );

      yield put(DialogsActions.setClose());
      if (!noRedirect) yield put(push(PATH.PASSENGER.BASE_URL));
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - createAppUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.createAppUserFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateAppUser({
  payload,
}: ReturnType<typeof UsersActions.updateAppUserRequest>): any {
  try {
    // @ts-ignore
    const userId = payload.id;

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Editando passageiro' }));
    const response = yield call(appUser.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.updateAppUserSuccess({
          appUserData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Passageiro alterado com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());

      if (window.location.pathname === PATH.PASSENGER.BASE_URL) {
        yield put(
          UsersActions.listAppUsersRequest({
            body: {
              _includes: 'transportcard,document,profile',
            },
          }),
        );
        return;
      }

      yield put(
        UsersActions.getAppUserRequest({
          id: userId,
          _includes: 'profile,balance,product,address',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateAppUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.updateAppUserFailure());
    yield put(DialogsActions.setClose());
  }
}

function* delAppUser({
  payload,
}: ReturnType<typeof UsersActions.deleteAppUserRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo passageiro' }));
    const response = yield call(appUser.delete, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Passageiro excluído com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(
        UsersActions.listAppUsersRequest({
          body: {
            _includes: 'document,profile',
          },
        }),
      );

      if (window.location.pathname !== PATH.PASSENGER.BASE_URL) {
        yield put(push(PATH.PASSENGER.BASE_URL));
      }
    }
  } catch (error) {
    // const internalCode: string =
    //   error.response && error.response.data.internalCode;
    // const message = errorMessagersList(internalCode);
    const message =
      'Erro ao remover usuário, o mesmo já possui dados. Favor entrar em contato com suporte.';
    console.error('AppUser - delAppUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.listAppUsersFailure());
    yield put(DialogsActions.setClose());
  }
}

function* toggleUserProfile({
  payload,
}: ReturnType<typeof UsersActions.userProfileToggleRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde...' }));

    const userId = window.location.pathname
      .split('/')
      .slice(-1)
      .join();
    const response = yield call(appUser.toggleProfile, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Status do perfil atualizado com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(UsersActions.userProfileToggleSuccess());
      yield put(
        UsersActions.getAppUserRequest({
          id: userId,
          _includes: 'profile,userprofile',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('AppUser - toggle User Profile - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.userProfileToggleFailure());
    yield put(DialogsActions.setClose());
  }
}

function* userProfileRenew({
  payload,
}: ReturnType<typeof UsersActions.userProfileRenewRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Aguarde...' }));

    const userId = window.location.pathname
      .split('/')
      .slice(-1)
      .join();
    const response = yield call(appUser.userProfileRenew, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Perfil renovado com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(UsersActions.userProfileRenewSuccess());
      yield put(
        UsersActions.getAppUserRequest({
          id: userId,
          _includes: 'profile,balance',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('AppUser - User Profile Renew - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.userProfileRenewFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createAppUserDocument({
  payload,
}: ReturnType<typeof UsersActions.createAppUserDocumentRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Cadastrando Documeno' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUserDocument.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.createAppUserDocumentSuccess({
          appUserDocumentData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: `Documento adicionado com sucesso`,
        }),
      );

      yield put(DialogsActions.setClose());
      yield window.location.reload();
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - createAppUserDocument - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.createAppUserDocumentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateAppUserDocument({
  payload,
}: ReturnType<typeof UsersActions.updateAppUserDocumentRequest>): any {
  try {
    const userId = window.location.pathname
      .split('/')
      .slice(-1)
      .join();

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Editando Documento' }));
    const response = yield call(appUserDocument.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.updateAppUserDocumentSuccess({
          appUserDocumentData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Documento alterado com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());

      if (window.location.pathname === PATH.PASSENGER.BASE_URL) {
        yield put(
          UsersActions.listAppUsersRequest({
            body: {
              _includes: 'transportcard,document,profile',
            },
          }),
        );
        return;
      }

      yield put(
        UsersActions.listAppUsersRequest({
          body: {
            id: userId,
            _includes: 'document',
          },
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateAppUserDocument - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.updateAppUserDocumentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* delAppUserDocument({
  payload,
}: ReturnType<typeof UsersActions.deleteAppUserDocumentRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo Documento' }));
    const response = yield call(appUserDocument.delete, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Documento excluído com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
      yield put(
        UsersActions.listAppUsersRequest({
          body: {
            _includes: 'document,profile',
          },
        }),
      );

      window.location.reload();
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('AppUser - delAppUser - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.listAppUsersFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createAppUserComment({
  payload,
}: ReturnType<typeof UsersActions.createAppUserCommentRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando Comentário' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUserComment.create, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.createAppUserCommentSuccess({
          appUserCommentData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: `Comentário adicionado com sucesso`,
        }),
      );

      yield put(
        UsersActions.getAppUserRequest({
          id: payload.userId,
          _includes: 'comment',
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - createAppUserComment - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.createAppUserCommentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateAppUserComment({
  payload,
}: ReturnType<typeof UsersActions.updateAppUserCommentRequest>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Atualizando Comentário' }),
    );
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUserComment.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.updateAppUserCommentSuccess({
          appUserCommentData: response.data,
        }),
      );

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: `Comentário atualizado com sucesso`,
        }),
      );

      yield put(
        UsersActions.getAppUserRequest({
          id: payload.userId,
          _includes: 'comment',
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateAppUserComment - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.updateAppUserCommentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* appUserFacialRegister({
  payload,
}: ReturnType<typeof UsersActions.appUserFacialRegisterRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Reenviando foto...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUser.appUserFacialRegister, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(UsersActions.appUserFacialRegisterSuccess());

      yield put(
        UsersActions.appUserCheckFacialListRequest({
          userId: payload.userId,
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - appUserFacialRegister - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.appUserFacialRegisterFailure());
    yield put(DialogsActions.setClose());
  }
}

function* deleteAppUserComment({
  payload,
}: ReturnType<typeof UsersActions.deleteAppUserCommentRequest>): any {
  try {
    const { id } = payload;

    yield put(DialogsActions.setOpenLoading({ title: 'Deletando Comentário' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUserComment.delete, {
      params: { transportOperatorId: transportOperator.id, id },
    });

    if (response.status === 200) {
      yield put(UsersActions.deleteAppUserCommentSuccess());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: `Comentário deletado com sucesso`,
        }),
      );

      yield put(
        UsersActions.getAppUserRequest({
          id: payload.userId,
          _includes: 'comment',
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - deleteAppUserComment - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.deleteAppUserCommentFailure());
    yield put(DialogsActions.setClose());
  }
}

function* getAppUserSettings({
  payload,
}: ReturnType<typeof UsersActions.getAppUserSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Carregando dados' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(appUserSettings.get, {
      params: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.getAppUserSettingsSuccess({
          appUserSettingsData: {
            ...response.data,
            operationMethods: response.data.operationMethods
              ? typeof response.data.operationMethods === 'string'
                ? JSON.parse(response.data.operationMethods)
                : response.data.operationMethods
              : null,
          },
        }),
      );
      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - appUserSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.getAppUserSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* updateAppUserSettings({
  payload,
}: ReturnType<typeof UsersActions.updateAppUserSettingsRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Editando configurações' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    const response = yield call(appUserSettings.update, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(
        UsersActions.updateAppUserSettingsSuccess({
          appUserSettingsData: {
            ...response.data,
            operationMethods: response.data.operationMethods
              ? typeof response.data.operationMethods === 'string'
                ? JSON.parse(response.data.operationMethods)
                : response.data.operationMethods
              : null,
          },
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Configurações alteradas com sucesso',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateAppUserSettings - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.updateAppUserSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* deleteAppUserSettings({
  payload,
}: ReturnType<typeof UsersActions.deleteAppUserSettingsRequest>): any {
  try {

    yield put(DialogsActions.setOpenLoading({ title: 'Deletando configurações' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(appUserSettings.delete, {
      params: { transportOperatorId: transportOperator.id},
    });

    if (response.status === 200) {
      yield put(UsersActions.deleteAppUserSettingsSuccess());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: `Configurações deletadas com sucesso`,
        }),
      );

      yield put(
        UsersActions.getAppUserSettingsRequest({}),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - deleteAppUserSettings - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.deleteAppUserSettingsFailure());
    yield put(DialogsActions.setClose());
  }
}

function* appUserSendUserTransactionViaEmail({
  payload,
}: ReturnType<
  typeof UsersActions.appUserSendUserTransactionViaEmailRequest
>): any {
  try {
    yield put(
      DialogsActions.setOpenLoading({ title: 'Efetuando  operação...' }),
    );
    const response = yield call(appUser.appUserSendUserTransactionViaEmail, {
      data: { ...payload },
    });

    if (response.status === 200) {
      yield put(UsersActions.appUserSendUserTransactionViaEmailSuccess());

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Operação realizada com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - appUserSendUserTransactionViaEmail - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.appUserSendUserTransactionViaEmailFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createSupervisorProfile({
  payload,
}: ReturnType<typeof UsersActions.createSupervisorProfileRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Salvando...' }));
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    const response = yield call(webUser.createSupervisorProfile, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(UsersActions.createSupervisorProfileSuccess());
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: `Perfis salvo com sucesso`,
        }),
      );
    }
    yield put(DialogsActions.setClose());
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - createSupervisorProfile - error', error);

    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.createSupervisorProfileFailure());
    yield put(DialogsActions.setClose());
  }
}

function* removeChargeRequest({
  payload,
}: ReturnType<typeof UsersActions.deleteChargeRequestRequest>): any {
  try {
    yield put(DialogsActions.setOpenLoading({ title: 'Excluindo pedido' }));
    const response = yield call(webUser.chargeRequestDelete, {
      params: { ...payload },
    });

    const storegeLimit =
      JSON.parse(window.localStorage.getItem('persist:root') as string)
        ?.localLimit || '10';
    const { localLimit } = JSON.parse(storegeLimit) || 10;

    if (response.status === 200) {
      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Pedido excluído com sucesso',
        }),
      );
      yield put(DialogsActions.setClose());
      yield put(UsersActions.deleteChargeRequestSuccess());
      yield put(
        OrdersActions.listOrdersRequest({
          type: 'USER',
          _includes: 'payment,user',
          _order: 'DESC',
          _sort: 'createdAt',
          isInstituition: true,
          _limit: localLimit,
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('orders - remove - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.deleteChargeRequestFailure());
    yield put(DialogsActions.setClose());
  }
}

function* createAppUserAddress({
  payload,
}: ReturnType<typeof UsersActions.createAppUserAddressRequest>): any {
  try {
    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );
    yield put(
      DialogsActions.setOpenLoading({ title: 'Cadastrando novo endereço' }),
    );
    const response = yield call(appUser.appUserCreateAddress, {
      data: { transportOperatorId: transportOperator.id, ...payload },
    });

    if (response.status === 200) {
      yield put(UsersActions.createAppUserAddressSuccess(true));

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Endereço criado com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        UsersActions.getAppUserRequest({
          id: (payload as { userId: string }).userId,
          _includes: 'address',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateAppUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.createAppUserAddressFailure());
    yield put(DialogsActions.setClose());
  }
}

function* deleteAppUserAddressSaga({
  payload,
}: ReturnType<typeof UsersActions.deleteAppUserAddressRequest>): any {
  try {
    const { id, userId } = payload;

    const transportOperator = yield select(
      (state) => state.transportOperators.transportOperatorSelected,
    );

    yield put(DialogsActions.setOpenLoading({ title: 'Deletando Endereço' }));

    const response = yield call(appUser.appUserDeleteAddress, {
      params: { transportOperatorId: transportOperator.id, id },
    });

    if (response.status === 200) {
      yield put(UsersActions.deleteAppUserAddressSuccess(true));

      yield put(
        SnackbarsActions.setOpen({
          open: true,
          type: 'success',
          message: 'Endereço deletado com sucesso',
        }),
      );

      yield put(DialogsActions.setClose());

      yield put(
        UsersActions.getAppUserRequest({
          id: userId,
          _includes: 'address',
        }),
      );
    }
  } catch (error) {
    const internalCode: string =
      error.response && error.response.data.internalCode;
    const message = errorMessagersList(internalCode);

    console.error('users - updateAppUser - error', error);
    yield put(
      SnackbarsActions.setOpen({
        open: true,
        type: 'error',
        message,
      }),
    );
    yield put(UsersActions.deleteAppUserAddressFailure());
    yield put(DialogsActions.setClose());
  }
}

function* refreshTokenSaga(): any {
  try {
    while (true) {
      yield delay(60000);

      const auth = getAuth();
      const user = auth.currentUser;

      if (user) {
        const newToken = yield call(() => user.getIdToken(true));
        setToken(newToken);
      }
    }
  } catch (error) {
    console.error('Erro ao atualizar token', error);
  }
}

// eslint-disable-next-line import/prefer-default-export
export const usersSagas = [
  takeLatest(UsersTypes.LOGIN_REQUEST, authLogin),
  takeLatest(UsersTypes.ENROLL_MFA_REQUEST, enrollMFA),
  takeLatest(UsersTypes.VERIFY_MFA_ENROLL_REQUEST, verifyAndEnrollMFA),
  takeLatest(UsersTypes.VERIFY_MFA_REQUEST, verifyMFASignIn),
  takeLatest(UsersTypes.UNENROLL_MFA_REQUEST, unenrollMFA),
  takeLatest(UsersTypes.LOGOUT_REQUEST, logout),
  takeLatest(UsersTypes.FORGOT_PASSWORD_REQUEST, forgotPassword),
  takeLatest(UsersTypes.CREATE_PASSWORD_REQUEST, createPassword),
  takeLatest(UsersTypes.RESET_PASSWORD_REQUEST, resetPassword),
  takeLatest(UsersTypes.RESET_PASSWORD_VERIFY_REQUEST, resetPasswordVerify),

  // Web Users
  takeLatest(UsersTypes.LIST_WEB_USERS_REQUEST, listWebUsers),
  takeLatest(
    UsersTypes.LIST_FROM_MODULES_WEB_USERS_REQUEST,
    listFromModulesWebUsers,
  ),
  takeLatest(UsersTypes.FILTER_WEB_USERS_REQUEST, filterWebUsers),
  takeLatest(UsersTypes.GET_WEB_USER_REQUEST, getWebUser),
  takeLatest(UsersTypes.CREATE_WEB_USER_REQUEST, createWebUser),
  takeLatest(UsersTypes.UPDATE_WEB_USER_REQUEST, updateWebUser),
  takeLatest(UsersTypes.UPDATE_WEB_USER_EMAIL_REQUEST, updateWebUserEmail),
  takeLatest(UsersTypes.DELETE_WEB_USER_REQUEST, delWebUser),
  takeLatest(
    UsersTypes.CREATE_SUPERVISOR_PROFILE_REQUEST,
    createSupervisorProfile,
  ),

  // App Users
  takeLatest(UsersTypes.LIST_APP_USERS_REQUEST, listAppUsers),
  takeLatest(UsersTypes.LIST_APP_USERS_MERGE_REQUEST, listAppUsersMerge),
  takeLatest(UsersTypes.FILTER_APP_USERS_MERGE_REQUEST, filterAppUsersMerge),
  takeLatest(UsersTypes.APP_USER_MERGE_REQUEST, appUserMerge),
  takeLatest(UsersTypes.GET_APP_USER_MERGE_REQUEST, getAppUserMerge),

  takeLatest(UsersTypes.GET_APP_USER_REQUEST, getAppUser),
  takeLatest(
    UsersTypes.GET_APP_USER_FROM_DOCUMENT_REQUEST,
    getAppUserFromDocument,
  ),
  takeLatest(
    UsersTypes.APP_USER_CHECK_FACIAL_LIST_REQUEST,
    appUserCheckFacialList,
  ),
  takeLatest(
    UsersTypes.APP_USER_FACIAL_REGISTER_REQUEST,
    appUserFacialRegister,
  ),
  takeLatest(UsersTypes.CREATE_APP_USER_REQUEST, createAppUser),
  takeLatest(UsersTypes.UPDATE_APP_USER_REQUEST, updateAppUser),
  takeLatest(UsersTypes.DELETE_APP_USER_REQUEST, delAppUser),
  takeLatest(UsersTypes.FILTER_APP_USERS_REQUEST, filterAppUsers),
  takeLatest(UsersTypes.USER_PROFILE_TOGGLE_REQUEST, toggleUserProfile),
  takeLatest(UsersTypes.USER_PROFILE_RENEW_REQUEST, userProfileRenew),

  takeLatest(
    UsersTypes.APP_USER_SEND_USER_TRANSACTION_BY_EMAIL_REQUEST,
    appUserSendUserTransactionViaEmail,
  ),

  // App Users In Company By Document
  takeLatest(
    UsersTypes.GET_APP_USER_IN_COMPANY_BY_DOCUMENT_REQUEST,
    getAppUserInConpanyByDocument,
  ),

  // App Users Document
  takeLatest(
    UsersTypes.CREATE_APP_USER_DOCUMENT_REQUEST,
    createAppUserDocument,
  ),
  takeLatest(
    UsersTypes.UPDATE_APP_USER_DOCUMENT_REQUEST,
    updateAppUserDocument,
  ),
  takeLatest(UsersTypes.DELETE_APP_USER_DOCUMENT_REQUEST, delAppUserDocument),

  // App User Comments
  takeLatest(UsersTypes.CREATE_APP_USER_COMMENT_REQUEST, createAppUserComment),
  takeLatest(UsersTypes.UPDATE_APP_USER_COMMENT_REQUEST, updateAppUserComment),
  takeLatest(UsersTypes.DELETE_APP_USER_COMMENT_REQUEST, deleteAppUserComment),

  // App User Settings
  takeLatest(UsersTypes.GET_APP_USER_SETTINGS_REQUEST, getAppUserSettings),
  takeLatest(UsersTypes.UPDATE_APP_USER_SETTINGS_REQUEST, updateAppUserSettings),
  takeLatest(UsersTypes.DELETE_APP_USER_SETTINGS_REQUEST, deleteAppUserSettings),

  // Charge Request
  takeLatest(UsersTypes.DELETE_CHARGE_REQUEST_REQUEST, removeChargeRequest),

  // App User Address
  takeLatest(UsersTypes.CREATE_APP_USER_ADDRESS_REQUEST, createAppUserAddress),
  takeLatest(
    UsersTypes.DELETE_APP_USER_ADDRESS_REQUEST,
    deleteAppUserAddressSaga,
  ),
];
